import {
	type ReactNode,
	createContext,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { captureUserContext, clearUserContext } from "../sentry/sentry-init";
import { getKeycloak, keycloakInitOptions } from "./config";
import { applyImpersonation } from "./impersonation";
import type Keycloak from "keycloak-js";
import type { KeycloakProfile } from "keycloak-js";
import type { UserProfile } from "@/types/user.types";
import { useImpersonationStore } from "@/features/impersonation/lib/impersonation-store";
import { AppLoader } from "@/components/app-loader";
import { ROLES } from "@/constants/roles";
import { isOneOf } from "@/utils/is-one-of/is-one-of";

interface KeycloakTokenParsed {
	role?: Array<string>;
	sub: string;
	preferred_username?: string;
	username?: string;
	email?: string;
	given_name?: string;
	family_name?: string;
	email_verified?: boolean;
}

export interface AuthContextType {
	keycloak: Keycloak | null;
	initialized: boolean;
	authenticated: boolean;
	user: UserProfile | null;
	error: Error | null;
	login: (redirectUri?: string) => Promise<void>;
	logout: (redirectUri?: string) => Promise<void>;
	updateToken: (minValidity?: number) => Promise<boolean>;
}

export const AuthContext = createContext<AuthContextType | undefined>(
	undefined
);

interface AuthProviderProps {
	children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
	const keycloak = useMemo(() => getKeycloak(), []);

	const [initialized, setInitialized] = useState(false);
	const [authenticated, setAuthenticated] = useState(false);
	const [user, setUser] = useState<KeycloakProfile | null>(null);
	const [error, setError] = useState<Error | null>(null);

	// updateAuthState is used to update the auth state when the keycloak instance is updated
	const updateAuthState = useCallback(() => {
		setAuthenticated(keycloak.authenticated ?? false);

		if (keycloak.authenticated && keycloak.tokenParsed) {
			// Extract user information from the ID token
			const tokenParsed = keycloak.tokenParsed as KeycloakTokenParsed;
			const realmRoles = keycloak.realmAccess?.roles ?? [];

			const userProfile: UserProfile = {
				id: tokenParsed.sub,
				username: tokenParsed.preferred_username ?? tokenParsed.username ?? "",
				email: tokenParsed.email,
				firstName: tokenParsed.given_name,
				lastName: tokenParsed.family_name,
				enabled: true,
				emailVerified: tokenParsed.email_verified,
				roles: [
					...realmRoles.filter((role) => isOneOf(role, Object.values(ROLES))),
				],
			};

			setUser(userProfile);
			// Set Sentry user context when user is authenticated
			captureUserContext(userProfile);
		} else {
			setUser(null);
			// Clear Sentry user context when not authenticated
			clearUserContext();
		}
	}, [keycloak]);

	useEffect(() => {
		// mounted is used to prevent state updates when the component is unmounted
		let mounted = true;

		const initKeycloak = async () => {
			const startTime = performance.now();
			try {
				console.log("Starting Keycloak initialization...");

				// Set up event handlers BEFORE initialization
				// onTokenExpired is used to refresh the token when it expires
				keycloak.onTokenExpired = () => {
					if (!mounted) return;
					console.log("Token expired, refreshing...");
					void keycloak.updateToken(30).catch(() => {
						if (!mounted) return;
						console.error("Failed to refresh token");
						setError(() => new Error("Session expired. Please login again."));
						void keycloak.logout();
					});
				};

				keycloak.onAuthSuccess = () => {
					if (!mounted) return;
					console.log("Auth success");
					void updateAuthState();
				};

				keycloak.onAuthError = (error_) => {
					if (!mounted) return;
					console.error("Auth error:", error_);
				};

				keycloak.onAuthRefreshSuccess = () => {
					if (!mounted) return;
					console.log("Token refresh success");
					void updateAuthState();
				};

				keycloak.onAuthRefreshError = () => {
					if (!mounted) return;
					console.error("Token refresh error - forcing logout");
					setError(() => new Error("Session expired. Please login again."));
					void keycloak.logout();
				};

				keycloak.onAuthLogout = () => {
					if (!mounted) return;
					console.log("Auth logout");
					setAuthenticated(false);
					setUser(null);
					// Clear Sentry user context on logout
					clearUserContext();
				};

				keycloak.onReady = (isAuthenticated) => {
					if (!mounted) return;
					console.log("Keycloak ready, authenticated:", isAuthenticated);
				};

				const authResult = await keycloak.init(keycloakInitOptions);
				if (!mounted) return;

				const initTime = performance.now() - startTime;
				console.log(
					`Keycloak initialized in ${initTime.toFixed(2)}ms, authenticated:`,
					authResult
				);

				updateAuthState();
				setInitialized(true);
			} catch (error_) {
				// TODO: put sentry logs here + fix init logic (https://github.com/Battmobility/react-web/issues/32)
				if (!mounted) return;
				const err =
					error_ instanceof Error
						? error_
						: new Error("Failed to initialize Keycloak");
				console.error("Failed to initialize Keycloak:", err);
				setError(err);
				setInitialized(true);
			}
		};

		void initKeycloak();

		return () => {
			mounted = false;
			// Clean up event handlers
			keycloak.onTokenExpired = undefined;
			keycloak.onAuthSuccess = undefined;
			keycloak.onAuthError = undefined;
			keycloak.onAuthRefreshSuccess = undefined;
			keycloak.onAuthRefreshError = undefined;
			keycloak.onAuthLogout = undefined;
			keycloak.onReady = undefined;
		};
	}, [keycloak, updateAuthState]);

	// Runtime impersonation: react to Zustand store changes.
	// On set → swap user to the impersonated profile.
	// On clear → restore admin profile from the live Keycloak token.
	useEffect(() => {
		const unsubscribe = useImpersonationStore.subscribe((state, previous) => {
			if (state.token === previous.token) return;
			if (state.token) {
				const result = applyImpersonation(state.token);
				if (result) {
					setUser(result.user);
					setAuthenticated(true);
				}
			} else {
				updateAuthState();
			}
		});
		return unsubscribe;
	}, [updateAuthState]);

	async function withAuthErrorHandling<T>(
		operation: () => Promise<T>,
		errorMessage: string
	): Promise<T> {
		try {
			setError(null);
			return await operation();
		} catch (error_) {
			const err = error_ instanceof Error ? error_ : new Error(errorMessage);
			setError(err);
			throw err;
		}
	}

	const login = useCallback(
		(redirectUri?: string) =>
			withAuthErrorHandling(
				() => keycloak.login(redirectUri ? { redirectUri } : undefined),
				"Login failed"
			),
		[keycloak]
	);

	const logout = useCallback(
		(redirectUri?: string) =>
			withAuthErrorHandling(
				() =>
					keycloak.logout({
						redirectUri: redirectUri ?? `${window.location.origin}/login`,
					}),
				"Logout failed"
			),
		[keycloak]
	);

	const updateToken = useCallback(
		async (minValidity = 30): Promise<boolean> => {
			try {
				const refreshed = await keycloak.updateToken(minValidity);
				return refreshed;
			} catch (error_) {
				console.error("Failed to update token:", error_);
				return false;
			}
		},
		[keycloak]
	);

	const value = useMemo<AuthContextType>(
		() => ({
			keycloak,
			initialized,
			authenticated,
			user,
			error,
			login,
			logout,
			updateToken,
		}),
		[
			keycloak,
			initialized,
			authenticated,
			user,
			error,
			login,
			logout,
			updateToken,
		]
	);

	// Show loading state while Keycloak is initializing
	if (!initialized) {
		return <AppLoader data-testid="keycloak-loading-skeleton" />;
	}

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
